import { Box, BoxProps, Flex, Text } from '@everlywell/ui-kit';
import React from 'react';

import ReviewsBackground from './assets/ReviewsBackground.png';
import ReviewsBackgroundMobile from './assets/ReviewsBackgroundMobile.png';

type SlotOverride = {
  sx?: any;
};

type RootSlotOverride = SlotOverride & {
  background?: BoxProps['background'];
  backgroundMobile?: string;
  backgroundDesktop?: string;
};

export type StatCardSlotOverrides = {
  root?: RootSlotOverride;
  content?: SlotOverride;
  percentageText?: SlotOverride;
  descriptionContainer?: SlotOverride;
  descriptionText?: SlotOverride;
};

export type StatCardProps = BoxProps & {
  recommendedRatio: number;
  slots?: StatCardSlotOverrides;
};

const DEFAULT_BACKGROUND_MOBILE =
  `url(${ReviewsBackgroundMobile}) center center / cover no-repeat`;
const DEFAULT_BACKGROUND_DESKTOP =
  `url(${ReviewsBackground}) center 33% / 102% no-repeat`;

const getBackground = (
  background: BoxProps['background'],
  rootOverrides?: RootSlotOverride,
): BoxProps['background'] => {
  if (background) {
    return background;
  }

  if (rootOverrides?.background) {
    return rootOverrides.background;
  }

  if (rootOverrides?.backgroundMobile || rootOverrides?.backgroundDesktop) {
    return [
      rootOverrides.backgroundMobile ?? DEFAULT_BACKGROUND_MOBILE,
      rootOverrides.backgroundDesktop ??
      rootOverrides.backgroundMobile ??
      DEFAULT_BACKGROUND_DESKTOP,
    ];
  }

  return [DEFAULT_BACKGROUND_MOBILE, DEFAULT_BACKGROUND_DESKTOP];
};

const mergeSlotSx = (
  ...sxValues: Array<StatCardProps['sx'] | Record<string, any> | undefined>
): StatCardProps['sx'] => {
  const merged = sxValues.reduce<Record<string, any>>((acc, sxValue) => {
    if (!sxValue) {
      return acc;
    }

    if (Array.isArray(sxValue)) {
      return sxValue.reduce<Record<string, any>>((innerAcc, item) => {
        if (!item || typeof item === 'function') {
          return innerAcc;
        }

        if (Array.isArray(item)) {
          return {
            ...innerAcc,
            ...item.reduce<Record<string, any>>((nestedAcc, nestedItem) => {
              if (!nestedItem || typeof nestedItem === 'function') {
                return nestedAcc;
              }

              return {
                ...nestedAcc,
                ...nestedItem,
              };
            }, {}),
          };
        }

        return {
          ...innerAcc,
          ...item,
        };
      }, acc);
    }

    if (typeof sxValue === 'object') {
      return {
        ...acc,
        ...sxValue,
      };
    }

    return acc;
  }, {});

  return Object.keys(merged).length ? merged : undefined;
};

function StatCard({
  recommendedRatio,
  slots,
  sx,
  background: backgroundProp,
  ...rest
}: StatCardProps): JSX.Element {
  const recommendedPercentage = Math.round(recommendedRatio * 100);
  const background = getBackground(backgroundProp, slots?.root);

  return (
    <Box
      position={'relative'}
      backgroundColor={'eggplant.base'}
      // use 102% to have the background image cover the whole box
      background={background}
      borderRadius={'12px'}
      // desktop width from the design
      width={['100%', '551px']}
      display={'flex'}
      alignItems={'center'}
      justifyContent={'center'}
      paddingY={[5, 0]}
      paddingX={[6, 0]}
      {...rest}
      sx={mergeSlotSx(sx, slots?.root?.sx)}
    >
      <Flex
        flexDirection={['row', 'column']}
        gap={[4, 0]}
        alignItems={'center'}
        sx={slots?.content?.sx}
      >
        <Text
          fontSize={['7xl', '86px']}
          color={'eggplant.wash'}
          fontWeight={'normal'}
          sx={slots?.percentageText?.sx}
        >
          {recommendedPercentage}%
        </Text>

        <Flex
          position={['static', 'absolute']}
          alignItems={'center'}
          justifyContent={'center'}
          bottom={[0, 8]}
          sx={slots?.descriptionContainer?.sx}
        >
          <Text
            size={'lg'}
            color={'tints.white'}
            textAlign={['left', 'center']}
            sx={slots?.descriptionText?.sx}
          >
            Would recommend this test to a friend
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}

export default StatCard;
